import type { TeacherLedgerLine } from "../../../../Api/apiUtils";

export const LEDGER_TAB_STYLES: Record<string, { borderColor: string; amountColor: string }> = {
  CLASSES: { borderColor: "tl-border-emerald", amountColor: "tl-amount-emerald" },
  PENALTIES: { borderColor: "tl-border-rose", amountColor: "tl-amount-rose" },
  RENEWALS: { borderColor: "tl-border-blue", amountColor: "tl-amount-blue" },
  BONUSES: { borderColor: "tl-border-violet", amountColor: "tl-amount-violet" },
  DISPUTE: { borderColor: "tl-border-amber", amountColor: "tl-amount-amber" },
};

export const normalizeLedgerTabKey = (tab?: string | null): string =>
  String(tab ?? "").toUpperCase();

export const isAllLedgerTab = (tab?: string | null): boolean =>
  normalizeLedgerTabKey(tab) === "ALL";

export const isDisputeLedgerLine = (line: TeacherLedgerLine): boolean =>
  String(line.auditAction ?? "").trim().toUpperCase() === "DISPUTE";

export const canRejectDispute = (line: TeacherLedgerLine): boolean => {
  if (!isDisputeLedgerLine(line)) return false;
  if (line.pendingReview !== true) return false;
  const disputeStatus = String(line.disputeStatus ?? "").trim().toUpperCase();
  return disputeStatus !== "REJECTED" && disputeStatus !== "RESOLVED";
};

export type LedgerBadge = {
  text: string;
  className: string;
  dot?: boolean;
};

export const formatEntryTypeLabel = (entryType?: string): string => {
  if (!entryType) return "";
  return entryType
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
};

export type LedgerTransaction = {
  id: string | number;
  time: string | null;
  title: string;
  subtitle: string;
  detailLine: string;
  amount: number;
  borderColor: string;
  amountColor: string;
  badges: LedgerBadge[];
  pendingReview?: boolean;
  reason?: string | null;
  description?: string | null;
  isDispute?: boolean;
  canRejectDispute?: boolean;
  teacherId?: string | number | null;
};

export type LedgerDayGroup = {
  date: string;
  dateKey: string;
  dayTotal: number;
  dayPositive: boolean;
  transactions: LedgerTransaction[];
};

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatCurrency = (amount: number, showSign = true): string => {
  const abs = Math.abs(amount).toLocaleString("en-IN");
  if (!showSign) return `₹${abs}`;
  if (amount > 0) return `+₹${abs}`;
  if (amount < 0) return `-₹${abs}`;
  return `₹${abs}`;
};

export const formatLedgerTabLabel = (tab: string): string => {
  const label = String(tab).replace(/_/g, " ").toLowerCase();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
};

export const formatClassWorkingHours = (value: unknown): string => {
  const text = String(value ?? "").trim();
  if (!text || /^0+(\.0+)?\s*(hrs?)?$/i.test(text)) return "0";
  return text;
};

export const formatClassTime = (classtime?: string): string | null => {
  if (!classtime) return null;
  const [hours, minutes] = classtime.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
};

export const formatGroupDate = (dateKey: string): string => {
  const date = new Date(`${dateKey}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const compare = new Date(date);
  compare.setHours(0, 0, 0, 0);

  if (compare.getTime() === today.getTime()) {
    return `Today ${MONTH_SHORT[date.getMonth()]} ${date.getDate()}`;
  }
  if (compare.getTime() === yesterday.getTime()) {
    return `Yesterday ${MONTH_SHORT[date.getMonth()]} ${date.getDate()}`;
  }
  return `${MONTH_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

export const getLineDateKey = (line: TeacherLedgerLine): string =>
  line.classDate ||
  (line.occurredAt ? line.occurredAt.split("T")[0] : "unknown");

export const buildLineBadges = (line: TeacherLedgerLine): LedgerBadge[] => {
  const badges: LedgerBadge[] = [];

  if (line.classLevel) {
    badges.push({ text: line.classLevel, className: "tl-badge-amber" });
  }
  if (line.domestic === true) {
    badges.push({ text: "India", className: "tl-badge-teal" });
  } else if (line.INTL1) {
    badges.push({ text: "INTL1", className: "tl-badge-teal" });
  } else if (line.INTL2) {
    badges.push({ text: "INTL2", className: "tl-badge-teal" });
  }
  if (line.classtimeDiffs) {
    badges.push({ text: `${line.classtimeDiffs} mins`, className: "tl-badge-stone" });
  }
  if (line.uiFeeLabel) {
    badges.push({
      text: line.uiFeeLabel,
      className: (line.amount ?? 0) < 0 ? "tl-badge-rose-soft" : "tl-badge-emerald-soft",
    });
  }
  if (line.pendingReview) {
    badges.push({ text: "Pending review", className: "tl-badge-orange", dot: true });
  }
  if (line.entryType === "COMMUNITY_INCENTIVE") {
    const fromNotes = line.notes?.match(/studentCount=(\d+)/)?.[1];
    const studentCount = Number(line.communityStudentCount ?? fromNotes);
    if (Number.isFinite(studentCount) && studentCount > 0) {
      badges.push({
        text: `${studentCount} student${studentCount === 1 ? "" : "s"} joined`,
        className: "tl-badge-violet",
      });
    }
  }
  const slotType = line.notes?.match(/slotType=([^,]+)/)?.[1];
  if (slotType) {
    badges.push({
      text: `${slotType.toLowerCase().replace(/^./, (char) => char.toUpperCase())} slot`,
      className: "tl-badge-violet",
    });
  }
  if (isDisputeLedgerLine(line)) {
    const disputeLabel = line.disputeStatus
      ? `Dispute · ${formatLedgerTabLabel(line.disputeStatus)}`
      : "Dispute";
    badges.push({ text: disputeLabel, className: "tl-badge-orange", dot: true });
  }

  return badges;
};

export const lineToTransaction = (line: TeacherLedgerLine): LedgerTransaction => {
  const tabKey = normalizeLedgerTabKey(line.ledgerTab) || "CLASSES";
  const styles = LEDGER_TAB_STYLES[tabKey] ?? LEDGER_TAB_STYLES.CLASSES;
  const amount = line.amount ?? 0;
  const amountColor =
    amount < 0 ? "tl-amount-rose" : amount > 0 ? styles.amountColor : "tl-amount-muted";

  const isManualAdjustment = line.entryType === "MANUAL_ADJUSTMENT";
  const entryTypeLabel = formatEntryTypeLabel(line.entryType);
  const coursePart =
    line.courseDisplayName || (isManualAdjustment ? "Manual adjustment" : "");
  const detailLine = [coursePart, entryTypeLabel].filter(Boolean).join(" · ");

  return {
    id: line.id,
    time: formatClassTime(line.classtime),
    title: (line.studentDisplayName || "").trim(),
    subtitle: coursePart || entryTypeLabel || "—",
    detailLine,
    amount,
    borderColor: amount < 0 ? "tl-border-rose" : styles.borderColor,
    amountColor,
    badges: buildLineBadges(line),
    pendingReview: line.pendingReview === true,
    reason: line.reason?.trim() || null,
    description: line.description?.trim() || null,
    isDispute: isDisputeLedgerLine(line),
    canRejectDispute: canRejectDispute(line),
    teacherId: line.teacherId ?? null,
  };
};

export const buildLedgerTabs = (apiTabs?: string[], lines: TeacherLedgerLine[] = []): string[] => {
  const fromApi = (apiTabs ?? []).map((tab) => String(tab).trim()).filter(Boolean);
  if (fromApi.length) return fromApi;

  const seen = new Set<string>();
  const tabs = ["ALL"];
  lines.forEach((line) => {
    const tab = String(line.ledgerTab ?? "").trim();
    const key = normalizeLedgerTabKey(tab);
    if (!tab || key === "ALL" || seen.has(key)) return;
    seen.add(key);
    tabs.push(tab);
  });
  return tabs;
};

export const filterLedgerLines = (lines: TeacherLedgerLine[], activeFilter: string): TeacherLedgerLine[] => {
  if (isAllLedgerTab(activeFilter)) return lines;
  return lines.filter(
    (line) => normalizeLedgerTabKey(line.ledgerTab) === normalizeLedgerTabKey(activeFilter)
  );
};

export const groupLedgerLines = (lines: TeacherLedgerLine[]): LedgerDayGroup[] => {
  const groupedByDate = lines.reduce<Record<string, TeacherLedgerLine[]>>((groups, line) => {
    const dateKey = getLineDateKey(line);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(line);
    return groups;
  }, {});

  return Object.entries(groupedByDate)
    .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
    .map(([dateKey, groupLines]) => {
      const dayTotal = groupLines.reduce((sum, line) => sum + (line.amount ?? 0), 0);
      return {
        date: formatGroupDate(dateKey),
        dateKey,
        dayTotal,
        dayPositive: dayTotal >= 0,
        transactions: groupLines
          .sort(
            (lineA, lineB) =>
              new Date(lineB.occurredAt ?? 0).getTime() - new Date(lineA.occurredAt ?? 0).getTime()
          )
          .map(lineToTransaction),
      };
    });
};

export const getTeacherInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "T";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

export const toDateString = (year: number, month: number, day: number): string =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export const getMonthRange = (year: number, month: number) => {
  const fromDate = toDateString(year, month, 1);
  const lastDay = new Date(year, month + 1, 0).getDate();
  const toDate = toDateString(year, month, lastDay);
  return { fromDate, toDate };
};

export const parseSummaryAmount = (value: unknown): number => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  return parseFloat(String(value ?? 0).replace(/,/g, "").replace(/[^\d.-]/g, "")) || 0;
};

export const getSummaryAmountColorClass = (value: number): string => {
  if (value > 0) return "tl-amount-emerald";
  if (value < 0) return "tl-amount-rose";
  return "tl-amount-muted";
};

export type PayoutBreakdownRow = {
  key: string;
  label: string;
  countLabel?: string;
  amount: number;
  valueText?: string;
  dotClass: string;
  amountClass: string;
};

const summaryNumber = (value: unknown): number => Number(value || 0);

export const buildPayoutBreakdownFromSummary = (
  summary?: Record<string, unknown>
): PayoutBreakdownRow[] => {
  const s = summary ?? {};

  const classesAmount = parseSummaryAmount(s.PAID_CLASS_COMPLETED);
  const renewalsAmount = parseSummaryAmount(s.RENEWAL_INCENTIVE);
  const volumeBonusAmount = parseSummaryAmount(s.CLASS_COUNT_BONUS);
  const teacherNoShowAmount = parseSummaryAmount(s.TEACHER_NO_SHOW);
  const studentNoShowAmount = parseSummaryAmount(s.STUDENT_NO_SHOW);
  const studentTeacherNoShowAmount = parseSummaryAmount(s.STUDENT_TEACHER_NO_SHOW);
  const communityAmount = parseSummaryAmount(s.COMMUNITY_INCENTIVE);
  const demoConductsAmount = parseSummaryAmount(s.DEMO_CLASS_COMPLETED);
  const demoConversionsAmount = parseSummaryAmount(s.DEMO_TO_PAID_CONVERSION);

  const penaltyAmount =
    summaryNumber(s.TEACHER_LEAVE_EXHAUST_PENALTY) +
    summaryNumber(s.TEACHER_ONE_HOUR_LEAVE_PENALTY) +
    summaryNumber(s.TEACHER_EMERGENCY_LEAVE_PENALTY);
  const penaltyCount =
    summaryNumber(s.TEACHER_LEAVE_EXHAUST_PENALTY_COUNT) +
    summaryNumber(s.TEACHER_ONE_HOUR_LEAVE_PENALTY_COUNT) +
    summaryNumber(s.TEACHER_EMERGENCY_LEAVE_PENALTY_COUNT);

  const disputedCount =
    summaryNumber(s.PAID_CLASS_COMPLETED_ADJUSTMENT_COUNT) +
    summaryNumber(s.MANUAL_ADJUSTMENT_COUNT) +
    summaryNumber(s.RENEWAL_INCENTIVE_ADJUSTMENT_COUNT) +
    summaryNumber(s.CLASS_COUNT_BONUS_ADJUSTMENT_COUNT) +
    summaryNumber(s.TEACHER_NO_SHOW_ADJUSTMENT_COUNT) +
    summaryNumber(s.STUDENT_NO_SHOW_ADJUSTMENT_COUNT) +
    summaryNumber(s.STUDENT_TEACHER_NO_SHOW_ADJUSTMENT_COUNT) +
    summaryNumber(s.COMMUNITY_INCENTIVE_ADJUSTMENT_COUNT) +
    summaryNumber(s.TEACHER_LEAVE_EXHAUST_PENALTY_ADJUSTMENT_COUNT) +
    summaryNumber(s.TEACHER_ONE_HOUR_LEAVE_PENALTY_ADJUSTMENT_COUNT) +
    summaryNumber(s.TEACHER_EMERGENCY_LEAVE_PENALTY_ADJUSTMENT_COUNT) +
    summaryNumber(s.DEMO_TO_PAID_CONVERSION_ADJUSTMENT_COUNT) +
    summaryNumber(s.DEMO_CLASS_COMPLETED_ADJUSTMENT_COUNT);
  const disputedAmount =
    summaryNumber(s.PAID_CLASS_COMPLETED_ADJUSTMENT) +
    summaryNumber(s.MANUAL_ADJUSTMENT) +
    summaryNumber(s.RENEWAL_INCENTIVE_ADJUSTMENT) +
    summaryNumber(s.CLASS_COUNT_BONUS_ADJUSTMENT) +
    summaryNumber(s.TEACHER_NO_SHOW_ADJUSTMENT) +
    summaryNumber(s.STUDENT_NO_SHOW_ADJUSTMENT) +
    summaryNumber(s.STUDENT_TEACHER_NO_SHOW_ADJUSTMENT) +
    summaryNumber(s.COMMUNITY_INCENTIVE_ADJUSTMENT) +
    summaryNumber(s.TEACHER_LEAVE_EXHAUST_PENALTY_ADJUSTMENT) +
    summaryNumber(s.TEACHER_ONE_HOUR_LEAVE_PENALTY_ADJUSTMENT) +
    summaryNumber(s.TEACHER_EMERGENCY_LEAVE_PENALTY_ADJUSTMENT) +
    summaryNumber(s.DEMO_TO_PAID_CONVERSION_ADJUSTMENT) +
    summaryNumber(s.DEMO_CLASS_COMPLETED_ADJUSTMENT);

  return [
    {
      key: "classes",
      label: "Classes",
      countLabel: String(summaryNumber(s.PAID_CLASS_COMPLETED_COUNT)),
      amount: classesAmount,
      dotClass: "tl-dot-emerald",
      amountClass: getSummaryAmountColorClass(classesAmount),
    },
    {
      key: "class-working-hours",
      label: "Class Working hours",
      amount: 0,
      valueText: formatClassWorkingHours(s.classWorkingHours),
      dotClass: "tl-dot-emerald-soft",
      amountClass: "tl-amount-muted",
    },
    {
      key: "renewals",
      label: "Renewals",
      countLabel: String(summaryNumber(s.Renewal_text)),
      amount: renewalsAmount,
      dotClass: "tl-dot-blue",
      amountClass: getSummaryAmountColorClass(renewalsAmount),
    },
    {
      key: "volume-bonus",
      label: "Volume bonus",
      amount: volumeBonusAmount,
      dotClass: "tl-dot-teal",
      amountClass: getSummaryAmountColorClass(volumeBonusAmount),
    },
    {
      key: "teacher-no-shows",
      label: "Teacher no show",
      countLabel: String(summaryNumber(s.TEACHER_NO_SHOW_COUNT)),
      amount: teacherNoShowAmount,
      dotClass: "tl-dot-amber",
      amountClass: getSummaryAmountColorClass(teacherNoShowAmount),
    },
    {
      key: "student-no-shows",
      label: "Student no show",
      countLabel: String(summaryNumber(s.STUDENT_NO_SHOW_COUNT)),
      amount: studentNoShowAmount,
      dotClass: "tl-dot-amber",
      amountClass: getSummaryAmountColorClass(studentNoShowAmount),
    },
    {
      key: "student-teacher-no-shows",
      label: "Student Teacher no show",
      countLabel: String(summaryNumber(s.STUDENT_TEACHER_NO_SHOW_COUNT)),
      amount: studentTeacherNoShowAmount,
      dotClass: "tl-dot-amber",
      amountClass: getSummaryAmountColorClass(studentTeacherNoShowAmount),
    },
    {
      key: "community",
      label: "Community",
      countLabel: String(summaryNumber(s.COMMUNITY_INCENTIVE_COUNT)),
      amount: communityAmount,
      dotClass: "tl-dot-violet",
      amountClass: getSummaryAmountColorClass(communityAmount),
    },
    {
      key: "penalties",
      label: "Leave penalties",
      countLabel: String(penaltyCount),
      amount: penaltyAmount,
      dotClass: "tl-dot-rose",
      amountClass: getSummaryAmountColorClass(penaltyAmount),
    },
    {
      key: "demo-conducts",
      label: "Demo conducts",
      countLabel: String(summaryNumber(s.DEMO_CLASS_COMPLETED_COUNT)),
      amount: demoConductsAmount,
      dotClass: "tl-dot-violet",
      amountClass: getSummaryAmountColorClass(demoConductsAmount),
    },
    {
      key: "demo-conversions",
      label: "Demo conversions",
      countLabel: String(summaryNumber(s.DEMO_TO_PAID_CONVERSION_COUNT)),
      amount: demoConversionsAmount,
      dotClass: "tl-dot-violet",
      amountClass: getSummaryAmountColorClass(demoConversionsAmount),
    },
    {
      key: "disputed",
      label: "Disputed",
      countLabel: String(disputedCount),
      amount: disputedAmount,
      dotClass: "tl-dot-violet",
      amountClass: getSummaryAmountColorClass(disputedAmount),
    },
  ];
};

export const formatSummaryTotalInr = (summary?: Record<string, unknown>): string => {
  const total = summary?.TOTAL_INR;
  if (!total) return "₹0";
  return `₹${Number(total)}`;
};
